import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ExternalLink, FileText, Users, Brain, Hash, Globe } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { searchIndex, type SearchItem } from "@/data/searchIndex";

const groupIcon: Record<SearchItem["group"], typeof Search> = {
  Pages: FileText,
  "Home sections": Hash,
  "Nonlinear thinkers": Brain,
  "Founding members": Users,
  Elsewhere: Globe,
};

const GROUP_ORDER: SearchItem["group"][] = [
  "Pages",
  "Home sections",
  "Nonlinear thinkers",
  "Founding members",
  "Elsewhere",
];

// Strict substring matching (cmdk's default fuzzy match is too loose for a site search).
const scoreMatch = (value: string, search: string) => {
  const q = search.trim().toLowerCase();
  if (!q) return 1;
  const [title = "", rest = ""] = value.toLowerCase().split("\u0000");
  const terms = q.split(/\s+/);
  let score = 0;
  for (const term of terms) {
    if (title.startsWith(term)) score += 3;
    else if (title.includes(term)) score += 2;
    else if (rest.includes(term)) score += 1;
    else return 0;
  }
  return score / (terms.length * 3);
};

const SiteSearch = ({ variant = "icon" }: { variant?: "icon" | "full" }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleSelect = (item: SearchItem) => {
    setOpen(false);
    if (item.href) {
      window.open(item.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (!item.to) return;
    const [path, hash] = item.to.split("#");
    navigate(path || "/");
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Search the website"
        className={
          variant === "full"
            ? "flex w-full items-center gap-2 rounded-full border border-border bg-card px-4 py-3 text-sm text-muted-foreground hover:border-primary/50 transition-colors min-h-[44px]"
            : "flex items-center justify-center h-11 w-11 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        }
      >
        <Search size={18} aria-hidden="true" />
        {variant === "full" && <span>Search the website</span>}
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} label="Search the website" filter={scoreMatch}>
        <CommandInput placeholder="Search pages, thinkers, members..." />
        <CommandList>
          <CommandEmpty>Nothing found. Try another word.</CommandEmpty>
          {GROUP_ORDER.map((group) => {
            const items = searchIndex.filter((i) => i.group === group);
            if (!items.length) return null;
            const Icon = groupIcon[group];
            return (
              <CommandGroup key={group} heading={group}>
                {items.map((item) => (
                  <CommandItem
                    key={`${group}-${item.title}-${item.to ?? item.href}`}
                    value={`${item.title}\u0000${item.subtitle ?? ""} ${item.keywords ?? ""} ${item.group}`}
                    onSelect={() => handleSelect(item)}
                    className="gap-3 data-[selected=true]:bg-primary/10"
                  >
                    <Icon className="shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">{item.title}</span>
                      {item.subtitle && (
                        <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                      )}
                    </span>
                    {item.href && (
                      <ExternalLink className="shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default SiteSearch;